/* VNYL-1478 / VNYL-1516 — Product detail V2 controller.
 *
 * Two jobs, both scoped to `.product-detail-v2`:
 *   1. Sticky Add to cart, at every width (desktop and tablet since VNYL-1593).
 *   2. Mirror the app-embed 10% badge into an inline strip.
 *
 * Why this exists rather than reusing the v1 behaviour: the mobile sticky bar
 * on v1 is driven from React (WishlistLargeIcon.js:67-96), which observes
 * `.product-summary .button-group` and writes inline styles onto
 * `.product-summary .wrap-button-buy`. V2's root is `.product-detail-v2`, so
 * that observer finds nothing and is inert by design — see the section's
 * header comment. float-buy-button.js is likewise not loaded here.
 */
(function () {
  'use strict';

  var root = document.querySelector('.product-detail-v2');
  if (!root) return;

  /* ─── 1. Sticky Add to cart ─────────────────────────────────────────── */

  /* The buy box's own button is what gets pinned, at every width, so the bar
   * can never disagree with the buy box (sold out, pre-order) and all.js
   * reports it as `sticky_bar` off `.pdv2-cta--stuck`.
   *
   * Below 768 it pins full-width whenever the buy box is off screen, in
   * either direction — unchanged from VNYL-1478.
   *
   * At 768 and up (VNYL-1593) it becomes a compact bar with the thumbnail,
   * title and price, and only once the whole button has scrolled out ABOVE
   * the viewport. At tablet heights the buy box can start below the fold, and
   * a bar on load would duplicate a button the shopper has not reached yet. */

  var card = root.querySelector('.pdv2-buy-card');
  var cta = root.querySelector('.pdv2-cta');
  var sentinel = root.querySelector('.pdv2-cta-sentinel');
  var footer = document.querySelector('.footer-bottom');
  var header = document.querySelector('.header-group');

  var mq = window.matchMedia('(max-width: 767.98px)');
  var sentinelVisible = true;
  var sentinelAbove = false;
  var footerVisible = false;
  var sentinelObserver = null;
  var footerObserver = null;
  var headerResizeObserver = null;

  function measureCta() {
    if (!cta) return;
    // Measure in flow, before anything is pinned, so the value is the real height.
    var h = cta.getBoundingClientRect().height;
    if (h > 0) root.style.setProperty('--pdv2-cta-h', Math.round(h) + 'px');
  }

  function measureHeader() {
    if (!header) return;
    var h = header.offsetHeight;
    if (h > 0) root.style.setProperty('--pdv2-header-h', Math.round(h) + 'px');
  }

  function applyStuck() {
    if (!cta || !card) return;
    var desktop = !mq.matches;
    var out = desktop ? !sentinelVisible && sentinelAbove : !sentinelVisible;
    // Mobile keeps the button pinned, slid away, at the footer even if the buy
    // box never left the screen; desktop only slides away a bar it has shown.
    var pinned = desktop ? out : out || footerVisible;
    var stuck = out && !footerVisible;

    cta.classList.toggle('pdv2-cta--stuck', pinned);
    cta.classList.toggle('pdv2-cta--hidden', pinned && footerVisible);
    card.classList.toggle('pdv2-buy-card--cta-out', pinned);
    document.body.classList.toggle('pdv2-cta-active', pinned);

    // Bar on screen, not slid away at the footer: lets the app's 10% pill lift
    // clear of it (see product-detail-v2.css). Measured once per state change,
    // not per scroll.
    document.body.classList.toggle('pdv2-cta-bar-visible', stuck);
    if (stuck) {
      var barH = cta.getBoundingClientRect().height;
      if (barH > 0) {
        document.documentElement.style.setProperty('--pdv2-cta-bar-h', Math.round(barH) + 'px');
      }
    }
  }

  function clearStuck() {
    if (cta) cta.classList.remove('pdv2-cta--stuck', 'pdv2-cta--hidden');
    if (card) card.classList.remove('pdv2-buy-card--cta-out');
    document.body.classList.remove('pdv2-cta-active', 'pdv2-cta-bar-visible');
  }

  function enableSticky() {
    if (!cta || !sentinel || sentinelObserver) return;
    measureCta();

    // The sentinel sits on top of the button, so at >=768 grow the root
    // upwards by the button's height: the bar takes over once the whole
    // button is gone, not as soon as its top edge leaves.
    var rootMargin = '0px';
    if (!mq.matches) {
      var ctaH = Math.round(cta.getBoundingClientRect().height);
      if (ctaH > 0) rootMargin = ctaH + 'px 0px 0px 0px';
    }

    sentinelObserver = new IntersectionObserver(
      function (entries) {
        sentinelVisible = entries[0].isIntersecting;
        sentinelAbove = entries[0].boundingClientRect.top < 0;
        applyStuck();
      },
      { threshold: 0, rootMargin: rootMargin }
    );
    sentinelObserver.observe(sentinel);

    if (footer) {
      footerObserver = new IntersectionObserver(
        function (entries) {
          footerVisible = entries[0].isIntersecting;
          applyStuck();
        },
        { threshold: 0 }
      );
      footerObserver.observe(footer);
    }
  }

  function disableSticky() {
    if (sentinelObserver) {
      sentinelObserver.disconnect();
      sentinelObserver = null;
    }
    if (footerObserver) {
      footerObserver.disconnect();
      footerObserver = null;
    }
    sentinelVisible = true;
    sentinelAbove = false;
    footerVisible = false;
    clearStuck();
  }

  // Mobile and desktop observe with different root margins, so a breakpoint
  // change rebuilds the observers rather than keeping the old ones.
  function syncToViewport() {
    disableSticky();
    enableSticky();
    if (!mq.matches) measureHeader();
  }

  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', syncToViewport);
  } else if (typeof mq.addListener === 'function') {
    mq.addListener(syncToViewport);
  }

  // The header compacts on scroll (float-header.js flips data-header-state),
  // which changes its height — re-measure rather than hard-coding an offset.
  if (header && typeof ResizeObserver !== 'undefined') {
    headerResizeObserver = new ResizeObserver(function () {
      if (!mq.matches) measureHeader();
    });
    headerResizeObserver.observe(header);
  }

  var resizeRaf = null;
  window.addEventListener(
    'resize',
    function () {
      if (resizeRaf) return;
      resizeRaf = window.requestAnimationFrame(function () {
        resizeRaf = null;
        // Only meaningful while the CTA is in flow.
        if (!document.body.classList.contains('pdv2-cta-active')) measureCta();
        if (!mq.matches) measureHeader();
      });
    },
    { passive: true }
  );

  if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
    document.fonts.ready.then(function () {
      if (!document.body.classList.contains('pdv2-cta-active')) measureCta();
    });
  }

  syncToViewport();
  measureHeader();

  /* ─── 1b. Save button forwards to the wishlist mount ────────────────── */

  /* The React wishlist paints a bare div and owns its own click, so it takes
   * no keyboard focus and carries no accessible name. The wrapper,
   * `.pdv2-action--save`, is the real control: it has the role, the label and
   * the tabindex, and forwards a tap or an Enter/Space to the mount. Clicks
   * that already landed on the mount are ignored, or the forward would fire
   * the handler twice and toggle the wishlist straight back off. */
  // iOS Safari only applies :active to elements with a touch listener in
  // their ancestry; the press state on the artwork buttons needs one.
  var plateActions = root.querySelector('.pdv2-plate-actions');
  if (plateActions) plateActions.addEventListener('touchstart', function () {}, { passive: true });

  var saveAction = root.querySelector('.pdv2-action--save');

  if (saveAction) {
    var forwardToWishlist = function (e) {
      var mount = saveAction.querySelector('.wishlist-icon') ||
        saveAction.querySelector('.react-wishlist-lg-icon');
      if (!mount) return;
      if (e.target !== saveAction && mount.contains(e.target)) return;
      mount.click();
    };

    saveAction.addEventListener('click', forwardToWishlist);
    saveAction.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        forwardToWishlist(e);
      }
    });
  }

  /* ─── 1c. Description clamp heights ─────────────────────────────────── */

  /* The read-more toggle is all.js's: it appends `.morelink` and flips `.less`
   * on `.inner-show-read-more`. Neither `-webkit-line-clamp` nor `height:auto`
   * can be transitioned, so the CSS animates max-height between two custom
   * properties and this measures them.
   *
   * Collapsed is four line-boxes. Expanded is the real content height, taken
   * with the clamp momentarily off — a fixed large max-height animates empty
   * space once the text has run out, and the easing visibly finishes early. */
  var descWrap = root.querySelector('.show-read-more');

  var measuringDescription = false;

  function measureDescription() {
    if (!descWrap || measuringDescription) return;
    var desc = descWrap.querySelector('.inner-show-read-more');
    if (!desc) return;
    measuringDescription = true;

    var cs = window.getComputedStyle(desc);
    var lh = parseFloat(cs.lineHeight);
    if (!lh || isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.5;
    root.style.setProperty('--pdv2-desc-clamped', Math.round(lh * 4) + 'px');
    // The read-more link sits on the last clamped line, so it has to share
    // the description's line box rather than its own 14px one.
    root.style.setProperty('--pdv2-desc-lh', lh + 'px');

    // Measure without animating the measurement itself.
    var hadLess = desc.classList.contains('less');
    var prevTransition = desc.style.transition;
    desc.style.transition = 'none';
    if (hadLess) desc.classList.remove('less');
    var full = desc.scrollHeight;
    if (hadLess) desc.classList.add('less');
    void desc.offsetHeight; // flush, so restoring the class does not animate
    desc.style.transition = prevTransition;

    if (full > 0) root.style.setProperty('--pdv2-desc-full', full + 'px');
    measuringDescription = false;
  }

  if (descWrap) {
    // all.js appends `.morelink` after its own init, so wait for that rather
    // than measuring an unclamped block on first paint — then stop watching.
    //
    // Both halves of that matter:
    //
    // childList only, not attributes. measureDescription() toggles `.less`
    // and writes `style.transition` to take its reading, so an observer
    // watching attributes on this subtree re-triggers itself on every
    // measurement — an infinite loop that pegs the CPU and hangs the tab.
    //
    // And it disconnects after the first measurement, because all.js rewrites
    // the link's own text on every toggle ("Read more" <-> "Show less"). That
    // is a childList mutation inside this wrapper, so a live observer fires
    // mid-toggle and re-measures on the exact frame the height changes —
    // `transition: none` plus a forced reflow, which cancels the animation it
    // was supposed to be enabling. The heights depend on the text and the
    // column width, neither of which the toggle changes; resize re-measures.
    var descObserver = new MutationObserver(function () {
      if (!descWrap.querySelector('.morelink')) return;
      measureDescription();
      descObserver.disconnect();
    });
    descObserver.observe(descWrap, { childList: true, subtree: true });
    measureDescription();
    if (descWrap.querySelector('.morelink')) descObserver.disconnect();

    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(measureDescription);
    }

    // The text reflows at a new width, so both heights have to be retaken.
    var descRaf = null;
    window.addEventListener(
      'resize',
      function () {
        if (descRaf) return;
        descRaf = window.requestAnimationFrame(function () {
          descRaf = null;
          measureDescription();
        });
      },
      { passive: true }
    );
  }

  /* ─── 1d. Animate the description toggle in both directions ─────────── */

  /* all.js owns the toggle: it flips `.less` and rewrites the link's text.
   * This watches that class and drives the height itself, because letting CSS
   * react to `.less` alone only animates one way.
   *
   * Expanding ends on `max-height: none` rather than the measured value. The
   * measurement can go stale — a late webfont reflows the text taller — and a
   * stale value silently clips the last lines of the description. Releasing
   * the cap once the motion is over makes that impossible. */
  var descEl = descWrap && descWrap.querySelector('.inner-show-read-more');
  var descState = null;
  var descDone = null;
  var descTimer = null;

  function animateDescription(toOpen) {
    if (!descEl) return;
    var full = parseFloat(root.style.getPropertyValue('--pdv2-desc-full'));
    var clamped = parseFloat(root.style.getPropertyValue('--pdv2-desc-clamped'));
    if (!full || !clamped) return; // nothing measured yet — let CSS do it

    if (descDone) descDone();

    // Only the collapse needs the clamp suspended, and it has to go on BEFORE
    // the start height is set: all.js has already added `.less` by the time
    // this runs, so the clamp is live and the box has already snapped to four
    // lines. Suspending it first puts the content back to full height so
    // there is something to animate down from.
    if (!toOpen) descEl.classList.add('pdv2-desc--animating');

    // The start value comes from the measurements, not from reading the
    // element. Reading it here returns the already-clamped 83px on a collapse,
    // and animating 83 -> 83 is what made it look instant.
    var from = toOpen ? clamped : full;
    descEl.style.maxHeight = from + 'px';

    // A forced reflow, not requestAnimationFrame. The start value has to be
    // committed before the end value lands or the browser coalesces them into
    // one step — but rAF is throttled to a few frames per second in a
    // backgrounded tab, which stretches that commit well past the 320ms
    // transition and makes the animation fire erratically or not at all.
    // Reading offsetHeight flushes layout synchronously and does not care
    // whether the tab is visible.
    void descEl.offsetHeight;
    descEl.style.maxHeight = (toOpen ? full : clamped) + 'px';

    descDone = function (e) {
      if (e && e.propertyName && e.propertyName !== 'max-height') return;
      descEl.removeEventListener('transitionend', descDone);
      if (descTimer) clearTimeout(descTimer);
      descTimer = null;
      descDone = null;
      descEl.classList.remove('pdv2-desc--animating');
      // open: uncapped, so a reflow can never clip it.
      // closed: hand back to the stylesheet's clamped value.
      descEl.style.maxHeight = toOpen ? 'none' : '';
    };
    descEl.addEventListener('transitionend', descDone);
    // transitionend does not fire if the value did not actually change.
    descTimer = setTimeout(function () {
      if (descDone) descDone();
    }, 450);
  }

  if (descEl) {
    var lessObserver = new MutationObserver(function () {
      // measureDescription() toggles `.less` itself; ignore its own noise.
      if (measuringDescription) {
        lessObserver.takeRecords();
        return;
      }
      var next = descEl.classList.contains('less') ? 'closed' : 'open';
      if (next === descState) return; // our own style/class writes
      descState = next;
      animateDescription(next === 'open');
    });
    lessObserver.observe(descEl, { attributes: true, attributeFilter: ['class'] });
    descState = descEl.classList.contains('less') ? 'closed' : 'open';
  }

  /* ─── 2. 10% first-order offer mirror (VNYL-1516) ───────────────────── */

  /* The pill is NOT theme markup. It is `#collectinng-email-popup .widget`,
   * rendered by the vinyl-app `collecting-popup` app embed
   * (config/settings_data.json), injected site-wide and fixed bottom-right at
   * 16/16, z-index 999 — right over the bottom of the buy box. Its copy,
   * eligibility (email captured / dismissed for 7 days / cart non-empty) and
   * triggers all stay with the app; this mirrors its visibility into an inline
   * strip that sits alongside it, so the strip shows when the app would show
   * the pill, and stays when the pill is closed with × (see
   * `dismissedOnlyByClose()`).
   *
   * The pill is deliberately not suppressed. An earlier version hid it with an
   * !important rule, which also forced its computed display to `none`, so any
   * later style write on the pill (the app's own `bottom` sync, for one) made
   * this mirror read it as hidden and drop the strip while the pill came back.
   *
   * Contract: the selector below, and the app toggling the element's INLINE
   * `style.display` between 'block' and 'none' (its own `closeWidget()` /
   * `handleCloseWidget()`). If the embed changes either, the strip simply never
   * shows and the pill behaves exactly as it does today. Degraded, not broken.
   *
   * NB the theme also carries `#vinyl-welcome-discount-badge` rules
   * (overwrite.css:1699) and an add-to-cart hide for that id (all.js:893).
   * No such element exists in the current embed — that id is dead on both
   * counts. Deliberately not relied on here. */

  var offer = root.querySelector('.pdv2-offer');
  if (!offer) return;

  var BADGE_SELECTOR = '#collectinng-email-popup .widget';
  var badge = null;
  var badgeObserver = null;

  function badgeIsVisible(el) {
    if (!el || !el.isConnected) return false;
    var cs = window.getComputedStyle(el);
    return cs.display !== 'none' && cs.visibility !== 'hidden';
  }

  /* The pill's × is the shopper dismissing the corner pill, not the offer, so
   * the strip outlives it (Aidan, 02/10). The app shows the pill when the
   * popup has been closed once and the shopper has neither given an email nor
   * tapped × in the last seven days (`handleCloseWidget()`). This is true when
   * × is the only thing keeping the pill hidden. Same keys as the app. */
  function dismissedOnlyByClose() {
    try {
      return (
        localStorage.getItem('vinyl_welcome_close_modal') === 'true' &&
        localStorage.getItem('vinyl_welcome_discount_email_submitted') !== 'true' &&
        parseInt(localStorage.getItem('vinyl_welcome_discount_dismissed_until') || '0', 10) >
          Date.now()
      );
    } catch (e) {
      return false;
    }
  }

  // `badge` must still exist after a dismissal: tapping the strip opens the
  // popup through it.
  function syncOffer() {
    if (badgeIsVisible(badge) || (badge && dismissedOnlyByClose())) {
      offer.removeAttribute('hidden');
    } else {
      offer.setAttribute('hidden', '');
    }
  }

  // A pill already hidden by × never changes again, so an email submitted
  // from the strip's popup wouldn't reach the observer. Bootstrap's modal
  // events bubble, so re-check whenever the popup closes.
  document.addEventListener('hidden.bs.modal', syncOffer);

  function watchBadge(el) {
    badge = el;
    if (badgeObserver) badgeObserver.disconnect();
    badgeObserver = new MutationObserver(syncOffer);
    badgeObserver.observe(badge, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    syncOffer();
  }

  /* VNYL-1551 — `Offer Displayed` (offer_version: inline), once per page view.
   * The pill is fixed on screen, so the app counts it as seen when it shows.
   * The strip sits in the buy column and may never be scrolled to, so it only
   * counts once at least half of it is in the viewport. A hidden strip is
   * display:none and can't intersect, and un-hiding it inside the viewport
   * fires the observer — so one observer covers both. */
  if ('IntersectionObserver' in window) {
    var offerViewObserver = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || offer.hidden) return;
        offerViewObserver.disconnect();
        try {
          if (typeof utils !== 'undefined' && utils.mixpanelRetryWrapper) {
            utils.mixpanelRetryWrapper.track('Offer Displayed', {
              offer_version: 'inline',
              page_type: 'product',
            });
          }
        } catch (e) {
          // ignore tracking failures
        }
      },
      { threshold: 0.5 }
    );
    offerViewObserver.observe(offer);
  }

  // Proxy the tap to the app's own control so the popup opens exactly as it
  // would from the pill. The app tracks the open (`Popup Reopened`); the flag
  // below tells it the tap came from the strip, not the pill (VNYL-1551).
  //
  // Click the `.title` inside the widget, never the widget itself: the widget's
  // first child is its dismiss button (`handleCloseWidget()`, which hides the
  // offer for seven days). `.title` sits inside the `data-bs-toggle="modal"`
  // wrapper, and is the same node all.js clicks to auto-open the popup.
  function openOffer() {
    if (!badge) return;
    var opener = badge.querySelector('.title') || badge;
    if (typeof opener.click !== 'function') return;
    window.__vinylOfferSource = 'inline';
    opener.click();
  }

  offer.addEventListener('click', openOffer);
  offer.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      openOffer();
    }
  });

  // The embed injects late, so watch for it rather than assuming it is absent —
  // and keep watching. An earlier version disconnected once it found the badge,
  // which meant that if the app ever removed and re-rendered it, the mirror went
  // dead for the rest of the session with the strip stuck hidden.
  var bodyObserver = new MutationObserver(function () {
    var found = document.querySelector(BADGE_SELECTOR);
    if (found && found !== badge) {
      watchBadge(found);
    } else if (!found && badge) {
      // Badge went away: drop the stale reference and re-hide the strip.
      badge = null;
      if (badgeObserver) {
        badgeObserver.disconnect();
        badgeObserver = null;
      }
      syncOffer();
    }
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });

  var existing = document.querySelector(BADGE_SELECTOR);
  if (existing) watchBadge(existing);
})();
